import styled, { css } from 'styled-components';
import { fontSize } from '../Typography/mixins';
import { focusOutlineCss } from '../utils/focus-indicator';
import StyledMaterialIcon from '../Icon/styles';
export const getBackgroundColor = ({
  focused,
  transparent,
  disabled,
  theme
}) => {
  if (transparent) return 'transparent';
  if (focused) return theme.palette.backgroundPrimary;
  if (disabled) return theme.palette.backgroundStrong;
  return theme.palette.backgroundPrimary;
};
export const containerStyles = css`
  position: relative;
  display: flex;

  border-radius: ${props => props.theme.textInputBorderRadius};
  border: ${({
  'data-focused': focused,
  'data-error': error,
  'data-seamless': seamless,
  'data-transparent': transparent,
  theme
}) => {
  if (seamless || transparent) return 'none';
  if (focused) return theme.textInputFocusBorder;
  if (error) return theme.textInputErrorBorder;
  return theme.textInputBorder;
}};
  background-color: ${({
  'data-error': error,
  'data-transparent': transparent,
  theme
}) => {
  if (transparent) return 'transparent';
  return error ? theme.palette.semanticErrorBackground : theme.palette.backgroundPrimary;
}};
  color: ${props => props.theme.palette.content};
  width: 100%;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  box-shadow: ${({
  'data-focused': focused,
  'data-error': error,
  'data-seamless': seamless,
  'data-transparent': transparent,
  theme,
  $reverseFocus
}) => {
  if (seamless || transparent) return 'none';
  if (focused) return $reverseFocus ? theme.focusBoxShadowReverse : theme.focusBoxShadow;
  if (error) return `${theme.palette.semanticError} 0 0 0 1px`;
  return 'none';
}};
  outline: none;
`;
export const Container = styled.div`
  ${containerStyles};
`;
export const Label = styled.label`
  display: block;
  width: calc(100% - 2rem);
  background-color: ${({
  'data-floating': floating,
  'data-multiline': multiline,
  disabled,
  theme
}) => !disabled && floating && multiline ? theme.palette.backgroundPrimary : 'transparent'};
  font-size: ${({
  'data-floating': floating,
  'data-size': size = '',
  theme
}) => theme[`textInputLabel${floating}${size}FontSize`]};
  font-weight: ${({
  'data-size': size = '',
  theme
}) => theme[`textInputLabelFloating${size}FontWeight`]};
  color: ${({
  disabled,
  theme
}) => disabled ? theme.palette.contentDisabled : theme.palette.content};
  position: absolute;
  left: 1rem;
  top: ${({
  'data-floating': floating,
  'data-multiline': multiline,
  theme
}) => {
  if (multiline) {
    if (floating) return '0.75rem';
    return '1.5rem';
  }
  return theme[`textInputTop${floating}`];
}};
  transform: translateY(-50%);
  cursor: text;
  transition:
    font-size 0.2s,
    top 0.2s,
    color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: ${({
  'data-clickable': clickable
}) => clickable ? 'all' : 'none'};
`;
export const inputStyles = css`
  font-size: ${({
  'data-size': size,
  theme
}) => theme[`textInputFontSize${size}`]};
  font-weight: ${({
  'data-size': size,
  theme
}) => theme[`textInputWeight${size}`]};
  padding: ${({
  'data-floating': floating,
  'data-size': size,
  theme
}) => theme[`textInputPadding${floating}${size}`]};
  border-radius: ${props => props.theme.textInputBorderRadius};
  background-color: ${({
  'data-error': error,
  'data-focused': focused,
  'data-transparent': transparent,
  disabled,
  theme
}) => getBackgroundColor({
  error,
  focused,
  transparent,
  disabled,
  theme
})};
  width: 100%;
  height: ${({
  'data-height': height,
  'data-size': size,
  'data-multiline': multiline,
  theme
}) => height || (multiline ? '6rem' : theme[`textInputHeight${size}`])};
  border: none;
  color: ${({
  disabled
}) => disabled ? props => props.theme.palette.contentDisabled : props => props.theme.palette.content};

  line-height: normal;
  /* Firefox / Edge ¯\_(ツ)_/¯ (https://stackoverflow.com/questions/43314921/strange-input-widths-in-firefox-vs-chrome) */
  min-width: 0;

  &:focus {
    outline: none;
  }

  &::-ms-clear {
    display: none;
  }

  &::placeholder {
    opacity: ${({
  'data-show-placeholder': showPlaceholder
}) => showPlaceholder ? '1' : '0'};
    color: ${props => props.theme.palette.content};
    font-weight: ${props => props.theme.textInputLabelFloatingFontWeight};
    transition: all 0.2s;
  }
`;
export const Input = styled.input`
  ${inputStyles};
`;
export const TextArea = styled.textarea`
  ${inputStyles};
  padding-top: ${({
  'data-floating': floating
}) => floating ? '1.75rem' : '0.875rem'};
  resize: none;
  height: ${({
  'data-height': height
}) => height};
`;
export const InputLabelContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  background-color: ${({
  disabled,
  theme
}) => disabled ? theme.palette.backgroundStrong : ''};
  border-radius: ${props => props.theme.textInputBorderRadius};
`;
const iconContainerStyles = css`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  background-color: ${({
  $color: color,
  $error: error,
  $focused: focused,
  $disabled: disabled,
  theme
}) => {
  if (color) return theme.palette[color] || color;
  return getBackgroundColor({
    error,
    focused,
    disabled,
    theme
  });
}};
  padding: ${({
  $size,
  theme
}) => theme[`textInputPadding${$size}`]};
  ${StyledMaterialIcon} {
    margin: -1px 0; // fix SearchInput height
  }
`;
export const PrefixContainer = styled.div`
  ${iconContainerStyles}
  ${({
  $color
}) => $color ? undefined : 'padding-right: 2px;'}
  border-radius: ${props => props.theme.textInputBorderRadius} 0 0 ${props => props.theme.textInputBorderRadius};
  z-index: 1; // required to correctly render the focus outline when using an focusable icon
`;
export const SuffixContainer = styled.div`
  ${iconContainerStyles}
  ${({
  $color
}) => $color ? undefined : 'padding-left: 2px;'}
  border-radius: 0 ${props => props.theme.textInputBorderRadius} ${props => props.theme.textInputBorderRadius} 0;
  z-index: 1; // required to correctly render the focus outline when using an focusable icon
`;
export const ContextHelp = styled.div`
  &.expanded {
    box-shadow: 0 2px 6px 0 ${props => props.theme.palette.border};
    padding-bottom: 1rem;

    &:has(input:focus-visible) {
      ${focusOutlineCss}
    }

    // @README - Firefox doesn't support :has() by default yet, providing fallback
    @supports not selector(:has(*)) {
      ${focusOutlineCss}
    }
  }
`;
export const StyledCheckListContainer = styled.div`
  margin-left: ${({
  $isContextHelpActive
}) => $isContextHelpActive ? '1rem' : '0'};
  margin-top: 0.5rem;

  ul {
    margin: 0;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;
export const StyledCheckListText = styled.span`
  ${fontSize('xs')};
  color: ${({
  $isValid,
  theme
}) => $isValid ? theme.palette.actionablePrimary : 'inherit'};
`;
//# sourceMappingURL=styles.js.map